import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, MessageCircle, MapPin, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrustBadges } from "@/components/TrustBadges";
import { LazyExchangeMapView } from "@/components/LazyExchangeMapView";
import { notifyByEmail } from "@/lib/notify";
import { distanceKm, modeLabel, type ExchangeMode } from "@/lib/constants";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/requests/$id")({
  head: () => pageHead({ path: "/requests", title: "Swap request — Book Swap", description: "Review a book request, see who sent it, and accept or decline it on Book Swap.", noindex: true }),
  component: RequestDetail,
});

type Req = {
  id: string; book_id: string; requester_id: string; status: string; message: string | null; created_at: string;
  offered_book_id: string | null; owner_picks?: boolean | null;
};
type Book = { id: string; title: string; author: string; cover_url: string | null; user_id: string; lat: number | null; lng: number | null; exchange_mode?: string | null; is_donation?: boolean | null };
type Profile = {
  id: string; full_name: string | null; avatar_url: string | null; bio: string | null;
  favorite_quote: string | null; verified: boolean | null; completed_swaps: number | null;
  response_rate: number | null; strikes: number | null;
};

function RequestDetail() {
  const { id } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState<Req | null>(null);
  const [book, setBook] = useState<Book | null>(null);
  const [requester, setRequester] = useState<Profile | null>(null);
  const [owner, setOwner] = useState<Profile | null>(null);
  const [requesterCoords, setRequesterCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [offered, setOffered] = useState<Book | null>(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [shelf, setShelf] = useState<{ id: string; title: string; author: string }[]>([]);
  const [pick, setPick] = useState("");

  useEffect(() => { if (!authLoading && !user) navigate({ to: "/login" }); }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data: r } = await supabase.from("exchange_requests").select("*").eq("id", id).maybeSingle();
      if (cancelled) return;
      if (!r) { setNotFound(true); return; }
      setReq(r as Req);
      const [{ data: b }, { data: p }] = await Promise.all([
        supabase.from("books").select("id,title,author,cover_url,user_id,lat,lng,exchange_mode,is_donation").eq("id", (r as Req).book_id).maybeSingle(),
        supabase.from("public_profiles").select("id,full_name,avatar_url,bio,favorite_quote,verified,completed_swaps,response_rate,strikes").eq("id", (r as Req).requester_id).maybeSingle(),
      ]);
      if (cancelled) return;
      setBook(b as Book);
      setRequester(p as Profile);
      if ((b as Book)?.user_id) {
        const { data: op } = await supabase.from("public_profiles")
          .select("id,full_name,avatar_url,bio,favorite_quote,verified,completed_swaps,response_rate,strikes")
          .eq("id", (b as Book).user_id).maybeSingle();
        if (!cancelled) setOwner(op as Profile | null);
      }
      if ((r as Req).offered_book_id) {
        const { data: ob } = await supabase.from("books").select("id,title,author,cover_url,user_id,lat,lng").eq("id", (r as Req).offered_book_id!).maybeSingle();
        if (!cancelled) setOffered(ob as Book);
      }
      // When the requester asked the owner to choose, load their shelf.
      if ((r as Req).owner_picks) {
        const { data: sh } = await supabase.from("books")
          .select("id,title,author").eq("user_id", (r as Req).requester_id).eq("status", "available");
        if (!cancelled) setShelf(sh ?? []);
      }
      // Requester's fuzzy coords (already rounded to ~1km in DB) — try their own books' coords or any listing they own.
      const { data: rb } = await supabase.from("books").select("lat,lng").eq("user_id", (r as Req).requester_id).not("lat", "is", null).limit(1).maybeSingle();
      if (!cancelled && rb?.lat != null && rb?.lng != null) setRequesterCoords({ lat: rb.lat, lng: rb.lng });
    })();
    return () => {
      cancelled = true;
    };
  }, [id, user]);

  async function updateStatus(status: "accepted" | "declined" | "cancelled") {
    if (!req || !book) return;
    const needsPick = status === "accepted" && mode === "swap" && !req.offered_book_id;
    if (needsPick && !pick) return toast.error("Choose a book from their shelf first.");
    setBusy(true);
    const patch = needsPick ? { status, offered_book_id: pick } : { status };
    const { error } = await supabase.from("exchange_requests").update(patch).eq("id", req.id);
    setBusy(false);
    if (error) { console.error(error); return toast.error("Could not update the request."); }
    setReq({ ...req, status, offered_book_id: needsPick ? pick : req.offered_book_id });
    toast.success(`Request ${status}.`);
    if (user && book.user_id === user.id && status !== "cancelled") {
      notifyByEmail({ kind: "request-accepted", requestId: req.id });
    }
  }

  async function confirmHandover() {
    if (!req) return;
    setBusy(true);
    const { data, error } = await supabase.rpc("confirm_exchange_completion" as any, { _request_id: req.id });
    setBusy(false);
    if (error) { console.error(error); return toast.error("Could not confirm the handover."); }
    if (data === "completed") {
      setReq({ ...req, status: "completed" });
      toast.success(mode === "donate" ? "Handover confirmed — the book has left Book Swap." : "Handover confirmed 🎉");
    } else {
      setReq({ ...req, status: "completed" });
      toast.success("Handover confirmed 🎉");
    }
  }

  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;
  if (notFound) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">Request not found.</p></div>;
  if (!req || !book || !requester) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">Loading…</p></div>;

  const mode = ((book.exchange_mode as ExchangeMode) ?? (book.is_donation ? "donate" : "swap")) as ExchangeMode;
  const isOwner = book.user_id === user.id;
  const isRequester = req.requester_id === user.id;
  if (!isOwner && !isRequester) {
    return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">You don't have access to this request.</p></div>;
  }

  const dist = book.lat != null && book.lng != null && requesterCoords
    ? distanceKm(book.lat, book.lng, requesterCoords.lat, requesterCoords.lng)
    : null;

  // Always show the OTHER person on this page — never yourself.
  const other = isOwner ? requester : owner;
  const otherRole = isOwner ? "Requester" : "Book owner";
  if (!other) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">Loading…</p></div>;


  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/book/$id" params={{ id: book.id }} className="text-sm text-primary hover:underline">← Back to book</Link>
          <Link to="/exchanges" className="text-sm text-primary hover:underline">All chosen books →</Link>
        </div>
        <h1 className="mt-3 text-3xl font-bold">
          {mode === "donate" ? "Donation request" : mode === "lend" ? "Borrow request" : "Swap request"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{modeLabel(mode)}</p>
        <p className="mt-1 text-sm text-muted-foreground">Status: <span className="font-semibold capitalize text-foreground">{req.status}</span></p>

        <section className="mt-6 grid gap-4 md:grid-cols-[1fr_240px]">
          <div className="rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-dark">{otherRole}</p>
            <div className="mt-3 flex items-start gap-4">
              {other.avatar_url ? (
                <img src={other.avatar_url} alt={other.full_name ?? otherRole} className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-soft text-xl font-bold text-primary">
                  {other.full_name?.[0]?.toUpperCase() ?? "?"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Link to="/u/$id" params={{ id: other.id }} className="text-lg font-semibold hover:underline">
                  {other.full_name ?? "A neighbor"}
                </Link>
                {other.verified && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </span>
                )}
                <div className="mt-2">
                  <TrustBadges compact data={{
                    verified: other.verified,
                    completed_swaps: other.completed_swaps,
                    response_rate: other.response_rate,
                    strikes: other.strikes,
                  }} />
                </div>
                {other.favorite_quote && (
                  <p className="mt-3 border-l-2 border-primary/40 pl-3 font-serif text-sm italic text-muted-foreground">"{other.favorite_quote}"</p>
                )}
                {other.bio && <p className="mt-2 text-sm text-foreground/90">{other.bio}</p>}
                {req.message && (
                  <div className="mt-3 rounded-lg bg-muted p-3 text-sm">
                    <p className="text-xs font-semibold text-muted-foreground">{isOwner ? "Their message" : "Your message"}</p>
                    <p className="mt-1">{req.message}</p>
                  </div>
                )}
                {dist !== null && (
                  <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary-dark">
                    <MapPin className="h-3 w-3" /> ~{dist.toFixed(1)} km {isOwner ? "from your book" : "from this book"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <Link to="/book/$id" params={{ id: book.id }} className="block rounded-2xl bg-card p-4 shadow-[var(--shadow-card)] hover:bg-muted">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-dark">Requested book</p>
            <div className="mt-2 flex gap-3">
              {book.cover_url ? (
                <img src={book.cover_url} alt={book.title} className="h-20 w-14 rounded object-cover" />
              ) : (
                <div className="flex h-20 w-14 items-center justify-center rounded bg-primary-soft text-xs">📖</div>
              )}
              <div className="min-w-0">
                <p className="truncate font-semibold">{book.title}</p>
                <p className="truncate text-xs text-muted-foreground">{book.author}</p>
              </div>
            </div>
            {offered && (
              <div className="mt-3 border-t border-border pt-2">
                <p className="text-xs font-semibold text-muted-foreground">Offered in return</p>
                <p className="mt-1 truncate text-sm">{offered.title}</p>
              </div>
            )}
          </Link>
        </section>

        {book.lat != null && book.lng != null && requesterCoords && (
          <section className="mt-6">
            <p className="mb-2 text-sm font-semibold">Approximate meetup area</p>
            <LazyExchangeMapView owner={{ lat: book.lat, lng: book.lng }} requesterZone={requesterCoords} />
            <p className="mt-2 text-xs text-muted-foreground">
              📚 {isOwner ? "Your book location" : "Book location"} · 👤 {isOwner ? "Requester's" : "Your"} approximate zone (~1 km, exact address stays private).
            </p>
          </section>
        )}

        {isOwner && mode === "swap" && req.status === "pending" && !req.offered_book_id && (
          <section className="mt-6 rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
            <p className="font-semibold">
              {requester.full_name ?? "This reader"} asked you to choose — take a look at their shelf and pick any book you'd like in return.
            </p>
            <div className="mt-3 max-w-md">
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger><SelectValue placeholder="Choose a book in return" /></SelectTrigger>
                <SelectContent>
                  {shelf.length === 0
                    ? <SelectItem value="_" disabled>They have no available books right now</SelectItem>
                    : shelf.map((b) => <SelectItem key={b.id} value={b.id}>{b.title} — {b.author}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </section>
        )}

        <section className="mt-6 flex flex-wrap gap-2">
          <Link to="/messages" search={{ to: other.id, book: book.id }}>
            <Button variant="outline">
              <MessageCircle className="mr-2 h-4 w-4" />
              Message {other.full_name?.split(" ")[0] ?? (isOwner ? "the requester" : "the owner")}
            </Button>
          </Link>
          {isOwner && req.status === "pending" && (
            <>
              <Button disabled={busy} onClick={() => updateStatus("accepted")}><Check className="mr-2 h-4 w-4" />Accept</Button>
              <Button disabled={busy} variant="destructive" onClick={() => updateStatus("declined")}><X className="mr-2 h-4 w-4" />Decline</Button>
            </>
          )}
          {isOwner && req.status === "accepted" && (
            <Button disabled={busy} variant="outline" onClick={() => updateStatus("cancelled")}>Cancel swap</Button>
          )}
          {req.status === "accepted" && (
            <Button disabled={busy} className="bg-primary hover:bg-primary/90" onClick={confirmHandover}>
              <Check className="mr-2 h-4 w-4" />
              {mode === "donate" ? "Confirm handover" : "Confirm exchange complete"}
            </Button>
          )}
          {isRequester && req.status === "pending" && (
            <Button disabled={busy} variant="outline" onClick={() => updateStatus("cancelled")}>Withdraw request</Button>
          )}
        </section>
      </div>
    </div>
  );
}
